import React, { useState, useEffect, useRef } from 'react';
import { Icons } from './Icons';

const VirtualBob = ({ activeModule, userName }) => {
    const [isDismissed, setIsDismissed] = useState(() => localStorage.getItem('ts_bob_dismissed') === 'true');
    const [hasSeenIntro, setHasSeenIntro] = useState(() => localStorage.getItem('ts_bob_intro_v1_6_0') === 'true');
    const [isOpen, setIsOpen] = useState(!hasSeenIntro);
    
    const [clickCount, setClickCount] = useState(0);
    const [mood, setMood] = useState('helpful'); 
    const [isAnimating, setIsAnimating] = useState(false);
    const idleTimerRef = useRef(null);
    const patienceTimerRef = useRef(null);

    useEffect(() => {
        if (!isDismissed && hasSeenIntro) {
            setIsOpen(false); 
            const timer = setTimeout(() => setIsOpen(true), 500); 
            return () => clearTimeout(timer);
        }
        setClickCount(0);
        setMood('helpful');
    }, [activeModule, isDismissed, hasSeenIntro]);

    useEffect(() => {
        if (isOpen && mood === 'helpful') {
            idleTimerRef.current = setTimeout(() => setMood('idle'), 15000); 
        }
        return () => clearTimeout(idleTimerRef.current);
    }, [isOpen, mood]);

    const handleDismiss = () => {
        setIsDismissed(true); setIsOpen(false);
        localStorage.setItem('ts_bob_dismissed', 'true');
    };

    const handleAcknowledgeIntro = () => {
        setHasSeenIntro(true); setIsOpen(false);
        localStorage.setItem('ts_bob_intro_v1_6_0', 'true');
    };

    const wakeBobUp = () => {
        setIsDismissed(false); setIsOpen(true); 
        setClickCount(0); setMood('helpful');
        localStorage.setItem('ts_bob_dismissed', 'false');
    };

    const handleBobClick = () => {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 300); 
        
        clearTimeout(idleTimerRef.current);
        clearTimeout(patienceTimerRef.current);
        
        const newCount = clickCount + 1;
        setClickCount(newCount);
        setIsOpen(true);

        if (newCount >= 7) {
            setMood('annoyed');
            patienceTimerRef.current = setTimeout(() => {
                setClickCount(0);
                setMood('helpful');
            }, 10000);
        } else {
            setMood('helpful');
        }
    };

    const getDialogue = () => {
        if (!hasSeenIntro) {
            return (
                <div>
                    <p className="mb-3">Hey there, {userName || 'Operative'}. I'm Virtual Bob. The Tertiary Hub has successfully passed staging. Welcome to v1.6.0.</p>
                    <p className="mb-3">I'm your designated legacy interface assistant. The database is now configured as a full multimedia CDN. You can upload audio logs, video files, and 3D models straight from the interface.</p>
                    <p>Poke around. If you get lost, just click me.</p>
                    <button onClick={handleAcknowledgeIntro} className="mt-4 w-full bg-[#00ff41]/20 hover:bg-[#00ff41] text-[#00ff41] hover:text-black border border-[#00ff41] py-2 text-[10px] font-bold uppercase tracking-widest transition-colors">
                        ACKNOWLEDGE V1.6.0
                    </button>
                </div>
            );
        }

        if (mood === 'annoyed') {
            const annoyedQuotes = [
                "Are we done here? Poking the virtual door isn't going to stabilize the Resonance.",
                "I miss 1985. The interfaces were greener and the operators clicked less.",
                "Every time you poke me, a timeline fragments. I hope you're happy.",
                "I am a highly advanced legacy AI. Please treat me with some dignity."
            ];
            return annoyedQuotes[clickCount % annoyedQuotes.length];
        }

        if (mood === 'idle') return "Just let me know if you need anything. I'll just be here. Floating.";

        switch (activeModule) {
            case 'dashboard': return "Welcome to Mission Control. Use the 'Action' buttons below to jump exactly where you need to go, or read the Field Manual to understand how this whole machine works.";
            case 'manual': return "The Field Manual. If you actually read this, you won't have to keep poking me for answers.";
            case 'ops': return "Showrunner Ops. Use the Live Teleprompter to guide actors during events, and check the Story Braid to see how the active narrative threads overlap.";
            case 'timeline': return "The Timeline. This is the narrative backbone. Inject story beats here. If reality fractures, tag the event as a Temporal Anomaly. It creates a Door you can step through to view that isolated timeline branch.";
            case 'calendar': return "The Community Calendar. Schedule live drops, Catalyst events, or Bob's Doors meetups. Click on any date square to add an event, and use the 'Add to GCal' button to sync it.";
            case 'announcements': return "Public Updates Blog. Write and publish rich-text updates that stream directly to the public landing page. Tag them as 'Internal' if you only want the crew to see them.";
            case 'personnel': return "The Roster & Users. Keep track of your artists, crew, and registered players. You can promote players to field operatives here.";
            case 'comms': return "Secure BBS. This is for crew chatter. Drop images right into the chat. L4/L5 clearance required to spin up new threads.";
            case 'cipher': return "The Studio. Type a word to generate a Solomonic TAG sigil, or a sentence for a CRI Matrix. Add decorators to make it unique. Hit 'Save' to put it in the archive.";
            case 'matrix': return "The Rabbit Hole. Click 'Add Node' to drop lore, artifacts, and journals onto the board. Then click 'Add Vector' to connect them with a sigil. It auto-saves, so don't panic.";
            case 'map': return "Geospatial Tracking. Search an address in the top right, then click 'Anchor Payload' to lock an artifact or lore drop to those exact GPS coordinates. Need to wipe the board? Hit the Trash icon.";
            case 'doors':
            case 'artifacts':
            case 'lore':
            case 'concepts':
                return "The Archives. This is where you draft content. Remember: Keep it as 'DRAFT' while writing, and flip it to 'VERIFIED (LIVE)' when you want players to find it in the wild.";
            case 'inventory': return "Assets & Logistics. Use this to track physical real-world props. It generates printable barcodes for you. Promote an asset when it's ready for the writing team.";
            case 'journal': return "Temporal Logs. This is where you write intercepted transmissions, CRI science logs, or my personal frantic notes. Link these directly to Doors in the Matrix.";
            case 'characters': return "Character Bible. Use this dossier system to track the biographies, faction alignments, and known inventories of key operatives and targets.";
            case 'analytics': return "The Numbers. Only High Command gets to look at this. Make sure your Eye Protocol (bottom left) is active, or the system will lock you out.";
            default: return "I'm just a virtual door. What do I know?";
        }
    };

    const BobSprite = () => (
        <div className={`relative transition-all duration-500 ease-in-out origin-bottom ${activeModule === 'dashboard' ? 'w-24 h-24 md:w-32 md:h-32' : 'w-12 h-12 md:w-16 md:h-16'} ${isAnimating ? 'animate-squish' : 'animate-float'}`}>
            <style>{`
                @keyframes bobFloat { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
                @keyframes bobBlink { 0%, 96%, 98%, 100% { transform: scaleY(1); } 97%, 99% { transform: scaleY(0.1); } }
                @keyframes bobSquish { 0%, 100% { transform: scale(1, 1); } 50% { transform: scale(1.1, 0.85) translateY(4px); } }
                @keyframes footTap { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(-20deg); } }
                @keyframes armCrossL { 0% { transform: rotate(0deg); } 100% { transform: rotate(-45deg) translate(2px, -2px); } }
                @keyframes armCrossR { 0% { transform: rotate(0deg); } 100% { transform: rotate(45deg) translate(-2px, -2px); } }
                @keyframes eyeNarrow { 0% { transform: scaleY(1); } 100% { transform: scaleY(0.4) translateY(2px); } }
                
                .animate-float { animation: bobFloat 4s ease-in-out infinite; }
                .animate-blink { animation: bobBlink 5s infinite; transform-origin: center 11px; }
                .animate-squish { animation: bobSquish 0.3s ease-in-out; }
                .idle-tap { animation: footTap 0.5s ease-in-out infinite; transform-origin: 11px 28px; }
                .angry-arm-l { animation: armCrossL 0.3s forwards; transform-origin: 6px 15px; }
                .angry-arm-r { animation: armCrossR 0.3s forwards; transform-origin: 26px 15px; }
                .angry-eyes { animation: eyeNarrow 0.3s forwards; transform-origin: center 11px; }
            `}</style>
            <svg viewBox="0 0 32 32" className={`w-full h-full ${mood === 'annoyed' ? 'drop-shadow-[0_0_15px_rgba(255,0,0,0.8)]' : 'drop-shadow-[0_0_10px_rgba(0,255,65,0.8)]'}`}>
                <g transform="translate(0, 2)">
                    <rect x="6" y="2" width="20" height="26" rx="2" fill="#050505" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="2" className="transition-colors duration-300" />
                    <rect x="9" y="5" width="14" height="20" rx="1" fill="none" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1" opacity="0.3" />
                    <circle cx="21" cy="16" r="1.5" fill={mood === 'annoyed' ? '#ff0000' : '#00ff41'} />
                    
                    <g className={mood === 'annoyed' ? 'angry-eyes' : 'animate-blink'}>
                        <ellipse cx="12" cy="11" rx="3.5" ry="4.5" fill="#050505" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1.5" />
                        <ellipse cx="18" cy="11" rx="3.5" ry="4.5" fill="#050505" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1.5" />
                        <circle cx={mood === 'annoyed' ? 12 : 13} cy="11" r="1.5" fill={mood === 'annoyed' ? '#ff0000' : '#00ff41'} />
                        <circle cx={mood === 'annoyed' ? 18 : 19} cy="11" r="1.5" fill={mood === 'annoyed' ? '#ff0000' : '#00ff41'} />
                    </g>

                    {mood === 'annoyed' ? (
                        <path d="M 13 18 Q 15 16 17 18" fill="none" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round" />
                    ) : (
                        <path d="M 12 17 Q 15 19.5 18 17" fill="none" stroke="#00ff41" strokeWidth="1.5" strokeLinecap="round" />
                    )}
                    
                    <g className={mood === 'annoyed' ? 'angry-arm-l' : ''}>
                        <path d="M 6 15 L 1 12" fill="none" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1.5" strokeLinecap="round" />
                    </g>
                    
                    <g className={mood === 'annoyed' ? 'angry-arm-r' : ''}>
                        <path d="M 26 15 L 31 12" fill="none" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1.5" strokeLinecap="round" />
                    </g>

                    <g className={mood === 'idle' ? 'idle-tap' : ''}>
                        <path d="M 11 28 L 10 32" fill="none" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1.5" strokeLinecap="round" />
                    </g>
                    <g>
                        <path d="M 21 28 L 22 32" fill="none" stroke={mood === 'annoyed' ? '#ff0000' : '#00ff41'} strokeWidth="1.5" strokeLinecap="round" />
                    </g>
                </g>
            </svg>
        </div>
    );

    if (isDismissed) {
        return (
            <button onClick={wakeBobUp} className="fixed bottom-24 md:bottom-4 right-4 z-[2000] p-2 bg-[#050505] border border-gray-800 rounded-full text-gray-500 hover:text-[#00ff41] hover:border-[#00ff41] hover:bg-[#00ff41]/10 transition-all opacity-50 hover:opacity-100 shadow-lg group" title="Wake Virtual Bob">
                <Icons.Lightbulb size={16} />
                <span className="absolute right-10 top-1/2 -translate-y-1/2 bg-black border border-[#00ff41] text-[#00ff41] text-[8px] font-mono px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">VIRTUAL BOB</span>
            </button>
        );
    }

    return (
        <div className="fixed bottom-24 md:bottom-6 right-6 z-[2000] flex flex-col items-end pointer-events-none">
            <div className={`transition-all duration-300 origin-bottom-right ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'} mb-4 pointer-events-auto`}>
                <div className={`bg-[#050505] border-2 ${mood === 'annoyed' ? 'border-red-500 shadow-[0_0_25px_rgba(255,0,0,0.25)]' : 'border-[#00ff41] shadow-[0_0_25px_rgba(0,255,65,0.25)]'} p-5 rounded-tl-xl rounded-tr-xl rounded-bl-xl w-72 md:w-80 relative transition-colors duration-300`}>
                    
                    <div className={`flex justify-between items-start border-b ${mood === 'annoyed' ? 'border-red-500/30' : 'border-[#00ff41]/30'} pb-2 mb-3 transition-colors duration-300`}>
                        <div className={`text-[10px] font-bold uppercase tracking-widest ${mood === 'annoyed' ? 'text-red-500' : 'text-[#00ff41]'}`}>VIRTUAL BOB // v1.6.0</div>
                        <div className="flex gap-3">
                            <button onClick={() => setIsOpen(false)} className={`${mood === 'annoyed' ? 'text-red-500/50 hover:text-red-500' : 'text-[#00ff41]/50 hover:text-[#00ff41]'} transition-colors`} title="Minimize"><Icons.ChevronUp className="rotate-180" size={14}/></button>
                            <button onClick={handleDismiss} className={`${mood === 'annoyed' ? 'text-red-500/50 hover:text-red-500' : 'text-[#00ff41]/50 hover:text-red-500'} transition-colors`} title="Dismiss Permanently"><Icons.X size={14}/></button>
                        </div>
                    </div>
                    
                    <div className={`text-xs font-mono leading-relaxed ${mood === 'annoyed' ? 'text-red-500' : 'text-[#00ff41]'}`}>
                        {getDialogue()}
                    </div>

                    <div className={`absolute -bottom-3 right-6 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[12px] ${mood === 'annoyed' ? 'border-t-red-500' : 'border-t-[#00ff41]'} transition-colors duration-300`}></div>
                    <div className="absolute -bottom-2 right-[25px] w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-[#050505]"></div>
                </div>
            </div>
            
            <div className="pointer-events-auto cursor-pointer" onClick={handleBobClick}>
                <BobSprite />
            </div>
        </div>
    );
};

export default VirtualBob;